/**
 * Select a Launch Queue.
 */
import Chip from '@material-ui/core/Chip';
// @todo: Migrate to 'Select' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Select} from '@wandb/weave/components/Form/Select';
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon, IconMagicWandStick} from '@wandb/weave/components/Icon';
import React from 'react';
import {type FormatOptionLabelMeta} from 'react-select';

import {ResourceChip} from './ResourceChip';
import * as S from './SelectComponents.styles';
import {type JsonObject} from './utilTypes';

export type QueueOption = {
  isTeam: boolean;
  value: string;
  entity: string;
  project: string;
  queue: string;
  queueID: string;
  isPrivate: boolean;
  defaultResourceConfigID: string;
  resourceType: string;
  config: JsonObject;
};

// This option represents creating a new Starter queue
export const CREATE_QUEUE_PLACEHOLDER: QueueOption = {
  isTeam: false,
  value: '',
  entity: '',
  project: '',
  queue: '',
  queueID: '',
  isPrivate: false,
  defaultResourceConfigID: '',
  resourceType: 'local-container',
  config: {},
};

export type GroupedQueueOptions = {
  label: string;
  options: QueueOption[];
};

export type QueueOptions = QueueOption[] | GroupedQueueOptions[];

type SelectQueueProps = {
  options: QueueOptions;
  defaultQueueOption?: QueueOption;
  onSelectQueue: (option: QueueOption) => void;
};

export const isGroupedQueueOption = (
  options: QueueOptions
): options is GroupedQueueOptions[] => {
  for (const option of options) {
    if (!('label' in option)) {
      return false;
    }
  }
  return true;
};

export const getDefaultQueue = (queues: QueueOptions): QueueOption | null => {
  if (isGroupedQueueOption(queues)) {
    // If we are grouped we have at least one entity-level queue and one project-level queue.
    // In that case, select the first entity-level queue as the default.
    return queues[0].options[0];
  }
  // Select whatever the first option is. Might be to create a queue.
  return queues[0];
};

const OptionLabel = (
  props: QueueOption,
  meta: FormatOptionLabelMeta<QueueOption>
) => {
  const {entity, queue, isTeam, isPrivate, resourceType} = props;
  if (queue === '') {
    return (
      <S.SelectOptionLabel>
        <IconMagicWandStick style={{marginRight: 8}} />
        <S.SelectOptionLabel>Create a 'Starter' queue</S.SelectOptionLabel>
        <ResourceChip value="local-container" />
      </S.SelectOptionLabel>
    );
  }

  const isValue = meta.context === 'value'; // selected value, not menu
  const label = isValue ? `${entity}/${queue}` : queue;
  return (
    <S.SelectOptionLabel>
      <S.SelectOptionLabel>{label}</S.SelectOptionLabel>
      {isTeam && isPrivate && (
        <Chip icon={<Icon name="lock-closed" />} label="Private" />
      )}
      <ResourceChip value={resourceType} />
    </S.SelectOptionLabel>
  );
};

export const SelectQueue = ({
  options,
  defaultQueueOption,
  onSelectQueue,
}: SelectQueueProps) => {
  const onChange = (option: QueueOption | null) => {
    if (option) {
      onSelectQueue(option);
    }
  };

  if (isGroupedQueueOption(options)) {
    return (
      <Select<QueueOption, false, GroupedQueueOptions>
        options={options}
        defaultValue={defaultQueueOption}
        onChange={onChange}
        formatOptionLabel={OptionLabel}
        placeholder="Select a Launch queue"
        noOptionsMessage={({inputValue}) => {
          return !inputValue ? 'No queue available' : 'No results found';
        }}
      />
    );
  }

  return (
    <Select
      options={options}
      defaultValue={defaultQueueOption}
      formatOptionLabel={OptionLabel}
      placeholder="Select a Launch queue"
      onChange={onChange}
      noOptionsMessage={({inputValue}) => {
        return !inputValue ? 'No queue available' : 'No results found';
      }}
    />
  );
};
